import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { Redux } from '../..'
import {
  ApplicationUsersResponse,
  CompanyProductsResponse,
  CrunchTimeApplicationUser,
  CrunchTimeCompanyProduct,
  CrunchTimeLocation,
  CrunchTimePackageType,
  CrunchTimeRecipe,
  FetchCompanyProductsParams,
  FetchLocationsParams,
  FetchRecipesParams,
  FetchWasteAdjustmentsParams,
  GetWasteProductsResponse,
  LocationsResponse,
  PackageTypesResponse,
  PublishWastePayload,
  RecipesResponse,
  SaveWasteDayPayload,
  WasteAdjustmentsResponse,
  CrunchTimeInventoryAdjustment,
  CreateWasteEntryPayload,
  WasteLogEntryResponse,
  SaveWasteLogPayload,
  UpdateWasteLogPayload,
  roundQuantityForCrunchTime,
  WasteProductItem,
  resolveWasteInventoryHeaderFlagLetter,
  resolveInventoryStorageCode,
  isWasteProductStorageCodeOptional,
  WASTE_REASON_CODE_TO_LABEL,
  WASTE_CRUNCHTIME_ENTRY_USER,
  WASTE_CRUNCHTIME_PUBLISHED_ENTRY_USER,
  normalizeWasteMemoForApi
} from '@/src/types/apps/gygWasteLogging'
import {
  coerceCrunchTimeListEnvelope,
  coerceCrunchTimeLocationsResponse,
  parseCrunchTimeAxiosResponse
} from '@/src/utils/crunchTimeApiResponse'

interface EonWasteLoggingState {
  adjustments: WasteAdjustmentsResponse['data']
  adjustmentsLoading: boolean
  locations: CrunchTimeLocation[]
  locationsLoading: boolean
  locationDetails: CrunchTimeLocation | null
  locationDetailsLoading: boolean
  recipes: CrunchTimeRecipe[]
  recipesLoading: boolean
  packageTypes: CrunchTimePackageType[]
  packageTypesLoading: boolean
  applicationUsers: CrunchTimeApplicationUser[]
  applicationUsersLoading: boolean
  companyProducts: CrunchTimeCompanyProduct[]
  companyProductsLoading: boolean
  wasteProducts: WasteProductItem[]
  wasteProductsLoading: boolean
  wasteProductsHydratedForConcept: string | null
  savedDays: string[]
  saveLoading: boolean
  wasteLogEntries: WasteLogEntryResponse[]
  wasteLogDate: string | null
  wasteLogLoading: boolean

  /** All waste log rows in the open posting period (Waste Summary tab). */
  wasteLogPeriodEntries: WasteLogEntryResponse[]
  wasteLogPeriodStartDate: string | null
  wasteLogPeriodEndDate: string | null
  wasteLogPeriodLoading: boolean
  submitLoading: boolean
  isPublished: boolean
  publishReady: boolean
  success: string | null
  error: string | null
  primaryStorageCodes: Record<string, string>
}

const initialState: EonWasteLoggingState = {
  adjustments: [],
  adjustmentsLoading: false,
  locations: [],
  locationsLoading: false,
  locationDetails: null,
  locationDetailsLoading: false,
  recipes: [],
  recipesLoading: false,
  packageTypes: [],
  packageTypesLoading: false,
  applicationUsers: [],
  applicationUsersLoading: false,
  companyProducts: [],
  companyProductsLoading: false,
  wasteProducts: [],
  wasteProductsLoading: false,
  wasteProductsHydratedForConcept: null,
  savedDays: [],
  saveLoading: false,
  wasteLogEntries: [],
  wasteLogDate: null,
  wasteLogLoading: false,
  wasteLogPeriodEntries: [],
  wasteLogPeriodStartDate: null,
  wasteLogPeriodEndDate: null,
  wasteLogPeriodLoading: false,
  submitLoading: false,
  isPublished: false,
  publishReady: false,
  success: null,
  error: null,
  primaryStorageCodes: {}
}

const crunchTimeAdjustmentTransactionKey = (adj: CrunchTimeInventoryAdjustment): string | undefined => {
  const qh = adj.inventoryAdjustmentHeaderDetails
  if (qh?.transactionNumber != null && String(qh.transactionNumber).trim() !== '') {
    return String(qh.transactionNumber).trim()
  }
  const vh = adj.inventoryValueAdjustmentHeaderDetails as Record<string, unknown> | undefined
  if (vh) {
    const t = vh.transactionNumber ?? vh.TransactionNumber ?? vh.poNumber ?? vh.PoNumber
    if (t != null && String(t).trim() !== '') return String(t).trim()
  }

  return undefined
}

const crunchTimeDetailRecordMatchesTransaction = (d: CrunchTimeInventoryAdjustment, txNum: string): boolean => {
  const q = d.inventoryAdjustmentHeaderDetails?.transactionNumber
  if (q != null && String(q) === String(txNum)) return true
  const vh = d.inventoryValueAdjustmentHeaderDetails as Record<string, unknown> | undefined
  if (vh) {
    const t = vh.transactionNumber ?? vh.TransactionNumber ?? vh.poNumber ?? vh.PoNumber
    if (t != null && String(t) === String(txNum)) return true
  }

  return false
}

const EMPTY_ADJUSTMENTS: WasteAdjustmentsResponse = { success: true, count: 0, data: [] }

export const fetchWasteAdjustments = createAsyncThunk<WasteAdjustmentsResponse, FetchWasteAdjustmentsParams, Redux>(
  'gygEonWasteLogging/fetchWasteAdjustments',
  async (
    { locationCode, entryDate, includeNull = true, entryUser = WASTE_CRUNCHTIME_PUBLISHED_ENTRY_USER },
    { extra }
  ) => {
    const entryUserQuery = entryUser != null && String(entryUser).trim() !== '' ? String(entryUser).trim() : undefined
    const listParams = {
      locationCode,
      entryDate,
      ...(includeNull ? { includeNull: true } : {}),
      ...(entryUserQuery ? { entryUser: entryUserQuery } : {})
    }

    let headersResp
    try {
      headersResp = await extra.httpClient.get('crunchtime/inventory/adjustments', {
        params: listParams
      })
    } catch (err: any) {
      const status = err?.response?.status

      if (status === 404 || status === 403) {
        return EMPTY_ADJUSTMENTS
      }
      throw err
    }

    const { payload: adjPayload } = parseCrunchTimeAxiosResponse(headersResp)
    const result = coerceCrunchTimeListEnvelope(adjPayload, EMPTY_ADJUSTMENTS) as WasteAdjustmentsResponse

    if (!Array.isArray(result.data) || result.data.length === 0) {
      return result
    }

    const enriched = await Promise.all(
      result.data.map(async adj => {
        const txNum = crunchTimeAdjustmentTransactionKey(adj)
        if (!txNum) return adj

        try {
          const detailResp = await extra.httpClient.get('crunchtime/inventory/adjustments', {
            params: {
              locationCode,
              entryDate,
              transactionNumber: txNum,
              includeDetails: true,
              ...(includeNull ? { includeNull: true } : {}),
              ...(entryUserQuery ? { entryUser: entryUserQuery } : {})
            }
          })
          const { payload: detailPayload } = parseCrunchTimeAxiosResponse(detailResp)
          const detailResult = coerceCrunchTimeListEnvelope(
            detailPayload,
            EMPTY_ADJUSTMENTS
          ) as WasteAdjustmentsResponse

          const match = Array.isArray(detailResult.data)
            ? detailResult.data.find(d => crunchTimeDetailRecordMatchesTransaction(d, txNum))
            : undefined

          return match ?? adj
        } catch {
          return adj
        }
      })
    )

    return { ...result, data: enriched }
  }
)

const CRUNCHTIME_INVENTORY_SINGLE = 'crunchtime/inventory'
const CRUNCHTIME_INVENTORY_RECIPE_BULK = 'crunchtime/inventory/inventory-recipe'
const CRUNCHTIME_INVENTORY_ADJUSTMENT_BULK = 'crunchtime/inventory/inventory-adjustment'

/** CrunchTime allows at most this many detail lines per saveInventoryAdjustments bulk call. */
const CRUNCHTIME_BULK_LINES_PER_HTTP_REQUEST = 15

const splitLinesIntoSequentialBatches = <T>(lines: T[], linesPerBatch: number): T[][] => {
  if (linesPerBatch <= 0 || lines.length <= linesPerBatch) return [lines]
  const batches: T[][] = []
  for (let i = 0; i < lines.length; i += linesPerBatch) {
    batches.push(lines.slice(i, i + linesPerBatch))
  }

  return batches
}

const PUBLISH_MAX_RETRIES = 3
const PUBLISH_INITIAL_BACKOFF_MS = 1000

const postWithRetry = async <T>(postFn: () => Promise<T>, retries = PUBLISH_MAX_RETRIES): Promise<T> => {
  let lastError: any
  let delayMs = PUBLISH_INITIAL_BACKOFF_MS
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await postFn()
    } catch (err: any) {
      lastError = err
      const status = err?.response?.status
      const isRetryable = status === 429 || (status >= 500 && status < 600) || !err?.response
      if (attempt === retries || !isRetryable) throw err
      await new Promise(r => setTimeout(r, delayMs))
      delayMs *= 2
    }
  }
  throw lastError
}

const splitWasteEntryPayloadsByRecipeFlag = (
  payloads: CreateWasteEntryPayload[]
): {
  recipeY: CreateWasteEntryPayload[]
  inventoryN: CreateWasteEntryPayload[]
} => {
  const recipeY: CreateWasteEntryPayload[] = []
  const inventoryN: CreateWasteEntryPayload[] = []
  for (const p of payloads) {
    if (resolveWasteInventoryHeaderFlagLetter(p.productId, p.userDefinedCategoryCode) === 'Y') {
      recipeY.push(p)
    } else {
      inventoryN.push(p)
    }
  }

  return { recipeY, inventoryN }
}

const postCrunchtimeWasteInventoryBulkBatches = async (
  httpClient: Redux['extra']['httpClient'],
  locationId: string,
  payloads: CreateWasteEntryPayload[],
  useRetry: boolean
): Promise<void> => {
  const { recipeY, inventoryN } = splitWasteEntryPayloadsByRecipeFlag(payloads)
  const run = useRetry ? (fn: () => Promise<unknown>) => postWithRetry(fn) : (fn: () => Promise<unknown>) => fn()

  if (recipeY.length) {
    const recipeBatches = splitLinesIntoSequentialBatches(recipeY, CRUNCHTIME_BULK_LINES_PER_HTTP_REQUEST)
    for (let b = 0; b < recipeBatches.length; b++) {
      await run(() =>
        httpClient.post(CRUNCHTIME_INVENTORY_RECIPE_BULK, {
          locationId,
          entries: recipeBatches[b]
        })
      )
    }
  }

  if (inventoryN.length) {
    const inventoryBatches = splitLinesIntoSequentialBatches(inventoryN, CRUNCHTIME_BULK_LINES_PER_HTTP_REQUEST)
    for (let b = 0; b < inventoryBatches.length; b++) {
      await run(() =>
        httpClient.post(CRUNCHTIME_INVENTORY_ADJUSTMENT_BULK, {
          locationId,
          entries: inventoryBatches[b]
        })
      )
    }
  }
}

/**
 * Unique products go out in bulk (up to 15 lines per request). Duplicate productIds each get
 * their own single-line CrunchTime adjustment so bulk does not repeat productNumber.
 */
const postCrunchtimeWasteInventoryToCrunchTime = async (
  httpClient: Redux['extra']['httpClient'],
  locationId: string,
  payloads: CreateWasteEntryPayload[],
  useRetry: boolean
): Promise<void> => {
  const productCounts = new Map<string, number>()
  for (const p of payloads) {
    const id = String(p.productId ?? '').trim()
    if (!id) continue
    productCounts.set(id, (productCounts.get(id) ?? 0) + 1)
  }

  const bulkPayloads: CreateWasteEntryPayload[] = []
  const duplicateLinePayloads: CreateWasteEntryPayload[] = []
  for (const p of payloads) {
    const id = String(p.productId ?? '').trim()
    if ((productCounts.get(id) ?? 0) > 1) {
      duplicateLinePayloads.push(p)
    } else {
      bulkPayloads.push(p)
    }
  }

  const run = useRetry ? (fn: () => Promise<unknown>) => postWithRetry(fn) : (fn: () => Promise<unknown>) => fn()

  if (bulkPayloads.length) {
    try {
      await postCrunchtimeWasteInventoryBulkBatches(httpClient, locationId, bulkPayloads, useRetry)
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Bulk inventory save failed for unique products'
      throw new Error(message)
    }
  }

  for (let i = 0; i < duplicateLinePayloads.length; i++) {
    const payload = duplicateLinePayloads[i]
    try {
      await run(() => httpClient.post(CRUNCHTIME_INVENTORY_SINGLE, payload))
    } catch (err: any) {
      const productId = payload.productId ?? 'unknown'
      const message =
        err?.response?.data?.message ||
        err?.message ||
        `Inventory save failed for duplicate product ${productId} (line ${i + 1}/${duplicateLinePayloads.length})`
      throw new Error(message)
    }
  }
}

export const saveWasteDayEntries = createAsyncThunk<string, SaveWasteDayPayload, Redux>(
  'gygEonWasteLogging/saveWasteDayEntries',
  async ({ locationId, date, entries }, { extra, rejectWithValue }) => {
    try {
      const transactionDate = new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })

      const payloads: CreateWasteEntryPayload[] = entries.map(entry => {
        const memoNorm = normalizeWasteMemoForApi(entry.memo)
        const memo = memoNorm ?? ''
        const udc =
          entry.userDefinedCategoryCode != null && String(entry.userDefinedCategoryCode).trim() !== ''
            ? String(entry.userDefinedCategoryCode).trim()
            : undefined
        const header: NonNullable<CreateWasteEntryPayload['header']> = {
          componentRecipeAdjustmentFlag: resolveWasteInventoryHeaderFlagLetter(entry.crunchTimeProductId, udc),
          entryUser: WASTE_CRUNCHTIME_ENTRY_USER
        }
        if (memoNorm != null) header.memo = memoNorm

        const storageTrimmed = resolveInventoryStorageCode(entry.storageCode)

        return {
          locationId,
          productId: entry.crunchTimeProductId,
          quantity: roundQuantityForCrunchTime(entry.quantity),
          qualitativeJudgment: memo ?? '',
          transactionDate,
          ...(entry.packageType ? { packageType: entry.packageType } : {}),
          ...(isWasteProductStorageCodeOptional(entry.crunchTimeProductId) || storageTrimmed === ''
            ? {}
            : { storageCode: storageTrimmed }),
          ...(udc != null ? { userDefinedCategoryCode: udc } : {}),
          header
        }
      })

      if (payloads.length === 0) {
        return rejectWithValue({ message: 'No waste entries to save to CrunchTime.' })
      }

      if (process.env.NODE_ENV !== 'production') {
        const { recipeY, inventoryN } = splitWasteEntryPayloadsByRecipeFlag(payloads)
        // eslint-disable-next-line no-console -- debug: CrunchTime inventory publish payloads
        console.log(
          '[EonWaste] POST waste to CrunchTime (saveWasteDayEntries)',
          JSON.stringify(
            {
              locationId,
              inventoryRecipeY: recipeY,
              inventoryAdjustmentN: inventoryN
            },
            null,
            2
          )
        )
      }

      await postCrunchtimeWasteInventoryToCrunchTime(extra.httpClient, locationId, payloads, false)

      return date
    } catch (error: any) {
      if (error.response) {
        const { message } = error.response.data ?? {}

        return rejectWithValue({ message: message || 'Failed to save waste entries to CrunchTime' })
      }

      return rejectWithValue({ message: error.message || 'Network error occurred' })
    }
  }
)

export const submitWasteEntries = createAsyncThunk<void, PublishWastePayload, Redux>(
  'gygEonWasteLogging/submitWasteEntries',
  async ({ locationId, daysToPublish, daypartCode, publishedEntryIds }, { extra, rejectWithValue }) => {
    try {
      for (const { entries } of daysToPublish) {
        for (const entry of entries) {
          if (entry.reason == null || String(entry.reason).trim() === '') {
            return rejectWithValue({
              message: `Reason is required to publish waste for product ${entry.crunchTimeProductId}.`
            })
          }
        }
      }

      const allPayloads: CreateWasteEntryPayload[] = daysToPublish.flatMap(({ date, entries }) => {
        const transactionDate = new Date(date).toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })

        return entries.map(entry => {
          const reasonCode = String(entry.reason).trim()
          const qualitativeJudgment = WASTE_REASON_CODE_TO_LABEL[reasonCode] ?? reasonCode

          const memoNorm = normalizeWasteMemoForApi(entry.memo)
          const udc =
            entry.userDefinedCategoryCode != null && String(entry.userDefinedCategoryCode).trim() !== ''
              ? String(entry.userDefinedCategoryCode).trim()
              : undefined
          const header: NonNullable<CreateWasteEntryPayload['header']> = {
            componentRecipeAdjustmentFlag: resolveWasteInventoryHeaderFlagLetter(entry.crunchTimeProductId, udc),
            entryUser: WASTE_CRUNCHTIME_ENTRY_USER
          }
          if (daypartCode != null) header.daypartCode = daypartCode
          if (memoNorm != null) header.memo = memoNorm

          const storageTrimmed = resolveInventoryStorageCode(entry.storageCode)

          return {
            locationId,
            productId: entry.crunchTimeProductId,
            quantity: roundQuantityForCrunchTime(entry.quantity),
            qualitativeJudgment,
            transactionDate,
            ...(entry.packageType ? { packageType: entry.packageType } : {}),
            ...(isWasteProductStorageCodeOptional(entry.crunchTimeProductId) || storageTrimmed === ''
              ? {}
              : { storageCode: storageTrimmed }),
            header,
            ...(udc != null ? { userDefinedCategoryCode: udc } : {})
          }
        })
      })

      if (allPayloads.length === 0) {
        return rejectWithValue({ message: 'No entries to publish.' })
      }

      let batchFailed: {
        message: string
        failed?: { productId: string; transactionDate: string; message: string }[]
      } | null = null
      try {
        await postCrunchtimeWasteInventoryToCrunchTime(extra.httpClient, locationId, allPayloads, true)
      } catch (err: any) {
        const message = err?.message || err?.response?.data?.message || 'Request failed'
        batchFailed = {
          message,
          failed: allPayloads.map(p => ({
            productId: p.productId,
            transactionDate: p.transactionDate,
            message
          }))
        }
      }

      if (batchFailed != null) {
        return rejectWithValue({
          message: `Publish failed (${allPayloads.length} entries). ${batchFailed.message}`,
          failed: batchFailed.failed
        })
      }

      if (publishedEntryIds?.length) {
        await extra.httpClient.post('eon/waste-log/mark-published', {
          entryIds: publishedEntryIds
        })
      }
    } catch (error: any) {
      if (error.response) {
        const { message } = error.response.data ?? {}

        return rejectWithValue({
          message: message || 'Failed to publish waste entries to CrunchTime'
        })
      }

      return rejectWithValue({ message: error.message || 'Network error occurred' })
    }
  }
)

/**
 * Parse waste log entry list from API JSON (GET /eon/waste-log).
 * POST responses also include `data`, but save uses a follow-up GET so Redux always holds the full DB list for the date (append-only saves; no dropped rows in UI).
 */
const wasteLogListFromApiEnvelope = (res: { data?: unknown }): WasteLogEntryResponse[] => {
  const top = res.data as { data?: unknown; savedCount?: number } | undefined
  const body = top?.data ?? top
  const list = Array.isArray(body)
    ? body
    : ((body as { data?: WasteLogEntryResponse[] } | null | undefined)?.data ?? [])

  return Array.isArray(list) ? list : []
}

export const fetchWasteLog = createAsyncThunk<
  { date: string; data: WasteLogEntryResponse[] },
  { locationCode: string; date: string },
  Redux
>('gygEonWasteLogging/fetchWasteLog', async ({ locationCode, date }, { extra }) => {
  const res = await extra.httpClient.get('eon/waste-log', {
    params: { locationCode, date }
  })

  return { date, data: wasteLogListFromApiEnvelope(res) }
})

export const fetchWasteLogForPostingPeriod = createAsyncThunk<
  { startDate: string; endDate: string; data: WasteLogEntryResponse[] },
  { locationCode: string; startDate: string; endDate: string },
  Redux
>('gygEonWasteLogging/fetchWasteLogForPostingPeriod', async ({ locationCode, startDate, endDate }, { extra }) => {
  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).startOf('day')
  const dates: string[] = []
  let cursor = start
  while (!cursor.isAfter(end, 'day')) {
    dates.push(cursor.format('YYYY-MM-DD'))
    cursor = cursor.add(1, 'day')
  }

  const lists = await Promise.all(
    dates.map(async date => {
      try {
        const res = await extra.httpClient.get('eon/waste-log', {
          params: { locationCode, date }
        })

        return wasteLogListFromApiEnvelope(res)
      } catch {
        return [] as WasteLogEntryResponse[]
      }
    })
  )

  return { startDate, endDate, data: lists.flat() }
})

export const saveWasteLog = createAsyncThunk<
  { date: string; data: WasteLogEntryResponse[]; savedCount: number },
  SaveWasteLogPayload,
  Redux
>('gygEonWasteLogging/saveWasteLog', async (payload, { extra, rejectWithValue }) => {
  try {
    const postRes = await extra.httpClient.post('eon/waste-log', payload)
    const top = postRes.data as { savedCount?: number; data?: unknown } | undefined
    const savedCount = top?.savedCount ?? payload.entries.filter(e => Number(e.quantity ?? 0) > 0).length

    // GET after POST (do not dispatch fetchWasteLog: its .rejected clears wasteLogEntries; inline GET keeps POST+reload atomic for errors).
    // Loads every active row for the date so Waste Summary lists all lines after multiple Log Waste saves.
    const getRes = await extra.httpClient.get('eon/waste-log', {
      params: { locationCode: payload.locationCode, date: payload.date }
    })

    return { date: payload.date, data: wasteLogListFromApiEnvelope(getRes), savedCount }
  } catch (error: any) {
    const message = error?.response?.data?.message || error?.message || 'Failed to save waste log'

    return rejectWithValue({ message })
  }
})

export const updateWasteLog = createAsyncThunk<number, UpdateWasteLogPayload, Redux>(
  'gygEonWasteLogging/updateWasteLog',
  async (payload, { extra, rejectWithValue }) => {
    try {
      const res = await extra.httpClient.patch('eon/waste-log', payload)
      const count = res.data?.data?.count ?? res.data?.count ?? payload.updates.length

      return count
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update waste log'

      return rejectWithValue({ message })
    }
  }
)

export const fetchCrunchTimeLocations = createAsyncThunk<LocationsResponse, FetchLocationsParams | void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocations',
  async (params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/locations', { params: params ?? {} })

    const { payload } = parseCrunchTimeAxiosResponse(response)

    return coerceCrunchTimeLocationsResponse(payload)
  }
)

export const fetchCrunchTimeLocationByCode = createAsyncThunk<CrunchTimeLocation, string, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocationByCode',
  async (locationCode, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/locations', {
      params: { locationCode }
    })

    const { payload } = parseCrunchTimeAxiosResponse(response)
    const body = coerceCrunchTimeLocationsResponse(payload)
    const location = Array.isArray(body.data) ? body.data[0] : null

    if (!location) {
      throw new Error(`CrunchTime location not found for code: ${locationCode}`)
    }

    return location
  }
)

export const fetchCrunchTimeLocationById = createAsyncThunk<CrunchTimeLocation, string, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeLocationById',
  async (locationId, { extra }) => {
    const response = await extra.httpClient.get(`crunchtime/locations/${locationId}`)

    const { payload } = parseCrunchTimeAxiosResponse<CrunchTimeLocation>(response)

    return payload
  }
)

export const fetchCrunchTimeRecipes = createAsyncThunk<RecipesResponse, FetchRecipesParams | void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeRecipes',
  async (params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/recipes-enhanced-v2', { params: params ?? {} })

    const { payload } = parseCrunchTimeAxiosResponse(response)

    return coerceCrunchTimeListEnvelope(payload, { success: true, count: 0, data: [] }) as RecipesResponse
  }
)

export const fetchCrunchTimePackageTypes = createAsyncThunk<PackageTypesResponse, void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimePackageTypes',
  async (_params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/package-types-v2')

    const { payload } = parseCrunchTimeAxiosResponse(response)

    return coerceCrunchTimeListEnvelope(payload, { success: true, count: 0, data: [] }) as PackageTypesResponse
  }
)

export const fetchCrunchTimeApplicationUsers = createAsyncThunk<ApplicationUsersResponse, void, Redux>(
  'gygEonWasteLogging/fetchCrunchTimeApplicationUsers',
  async (_params, { extra }) => {
    const response = await extra.httpClient.get('crunchtime/application-users')

    const { payload } = parseCrunchTimeAxiosResponse(response)

    return coerceCrunchTimeListEnvelope(payload, { success: true, count: 0, data: [] }) as ApplicationUsersResponse
  }
)

export const fetchCrunchTimeCompanyProducts = createAsyncThunk<
  CompanyProductsResponse,
  FetchCompanyProductsParams | void,
  Redux
>('gygEonWasteLogging/fetchCrunchTimeCompanyProducts', async (params, { extra }) => {
  const response = await extra.httpClient.get('crunchtime/company-products-enhanced-v1', {
    params: params ?? {}
  })

  const { payload } = parseCrunchTimeAxiosResponse(response)

  return coerceCrunchTimeListEnvelope(payload, { success: true, count: 0, data: [] }) as CompanyProductsResponse
})

const WASTE_PRODUCTS_CACHE_KEY_PREFIX = 'eon_waste_products_'
const WASTE_PRODUCTS_CACHE_TTL_MS = 15 * 60 * 1000 // 15 minutes

const getWasteProductsFromCache = (concept: string): GetWasteProductsResponse | null => {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(`${WASTE_PRODUCTS_CACHE_KEY_PREFIX}${concept}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { data: GetWasteProductsResponse['data']; concept: string; fetchedAt: number }
    if (!Array.isArray(parsed.data) || typeof parsed.fetchedAt !== 'number') return null
    if (Date.now() - parsed.fetchedAt > WASTE_PRODUCTS_CACHE_TTL_MS) return null

    return { concept: parsed.concept ?? concept, data: parsed.data }
  } catch {
    return null
  }
}

const setWasteProductsCache = (concept: string, payload: GetWasteProductsResponse): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(
      `${WASTE_PRODUCTS_CACHE_KEY_PREFIX}${concept}`,
      JSON.stringify({ data: payload.data, concept: payload.concept, fetchedAt: Date.now() })
    )
  } catch {
    // ignore cache write errors
  }
}

const PRIMARY_STORAGE_CACHE_KEY_PREFIX = 'eon_location_product_pricing_'
const PRIMARY_STORAGE_CACHE_TTL_MS = 15 * 60 * 1000 // 15 minutes (align with waste-products cache)

type PrimaryStorageSessionCache = { map: Record<string, string>; fetchedAt: number }

const readPrimaryStorageSessionCache = (locationCode: string): PrimaryStorageSessionCache | null => {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(`${PRIMARY_STORAGE_CACHE_KEY_PREFIX}${locationCode}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PrimaryStorageSessionCache
    if (!parsed || typeof parsed.map !== 'object' || parsed.map === null || typeof parsed.fetchedAt !== 'number') {
      return null
    }
    if (Date.now() - parsed.fetchedAt > PRIMARY_STORAGE_CACHE_TTL_MS) return null

    return parsed
  } catch {
    return null
  }
}

const writePrimaryStorageSessionCache = (locationCode: string, map: Record<string, string>): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    const payload: PrimaryStorageSessionCache = { map, fetchedAt: Date.now() }
    sessionStorage.setItem(`${PRIMARY_STORAGE_CACHE_KEY_PREFIX}${locationCode}`, JSON.stringify(payload))
  } catch {
    // ignore cache write errors
  }
}

const pickPrimaryStoragePayload = (requestedIds: string[], map: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const id of requestedIds) {
    const v = map[id]
    if (v != null && String(v).trim() !== '') {
      out[id] = String(v).trim()
    }
  }

  return out
}

export const fetchPrimaryStorageCodes = createAsyncThunk<
  Record<string, string>,
  { locationCode: string; productNumbers: string[] },
  Redux
>('gygEonWasteLogging/fetchPrimaryStorageCodes', async ({ locationCode, productNumbers }, { extra }) => {
  if (!locationCode?.trim() || productNumbers.length === 0) return {}

  const loc = locationCode.trim()
  const normalizedIds = [...new Set(productNumbers.map(p => String(p).trim()).filter(Boolean))]

  const cached = readPrimaryStorageSessionCache(loc)
  const mergedMap: Record<string, string> = cached ? { ...cached.map } : {}

  const missing = normalizedIds.filter(id => !Object.prototype.hasOwnProperty.call(mergedMap, id))

  if (missing.length === 0) {
    return pickPrimaryStoragePayload(normalizedIds, mergedMap)
  }

  const productNumbersParam = missing.slice(0, 100).join(',')
  const response = await extra.httpClient.get('crunchtime/location-product-pricing', {
    params: { locationCode: loc, productNumbers: productNumbersParam }
  })
  const { payload: raw } = parseCrunchTimeAxiosResponse<Record<string, unknown>>(response)
  const chunk: Record<string, unknown> =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}

  const normalizePricingStorageValue = (v: unknown): string => {
    if (v == null) return ''
    if (typeof v === 'string') return v.trim()
    if (typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>
      const code = o.storageCode ?? o.StorageCode ?? o.storage ?? o.Storage
      if (code != null && String(code).trim() !== '') return String(code).trim()
    }

    return ''
  }

  const batch = missing.slice(0, 100)
  for (const [k, v] of Object.entries(chunk)) {
    const key = String(k).trim()
    mergedMap[key] = normalizePricingStorageValue(v)
  }
  for (const id of batch) {
    if (!Object.prototype.hasOwnProperty.call(mergedMap, id)) mergedMap[id] = ''
  }

  writePrimaryStorageSessionCache(loc, mergedMap)

  return pickPrimaryStoragePayload(normalizedIds, mergedMap)
})

export const fetchWasteProducts = createAsyncThunk<GetWasteProductsResponse, { concept: string }, Redux>(
  'gygEonWasteLogging/fetchWasteProducts',
  async ({ concept }, { extra }) => {
    const conceptVal = (concept ?? '').trim() || 'AUWASTE'
    const cached = getWasteProductsFromCache(conceptVal)
    if (cached) {
      return cached
    }

    try {
      const response = await extra.httpClient.get('crunchtime/waste-products', {
        params: { concept: conceptVal }
      })
      const { payload: body } = parseCrunchTimeAxiosResponse<{
        concept?: string
        items?: WasteProductItem[]
        data?: WasteProductItem[]
      }>(response)
      const data = Array.isArray(body?.items) ? body.items : Array.isArray(body?.data) ? body.data : []
      const payload: GetWasteProductsResponse = {
        concept: body?.concept ?? conceptVal,
        data
      }
      setWasteProductsCache(conceptVal, payload)

      return payload
    } catch (err: unknown) {
      throw err
    }
  }
)

const gygEonWasteLoggingSlice = createSlice({
  name: 'gygEonWasteLogging',
  initialState,
  reducers: {
    resetWasteLoggingState(state) {
      state.success = null
      state.error = null
    },
    setPublished(state, action: { payload: boolean }) {
      state.isPublished = action.payload
    },
    setPublishReady(state, action: { payload: boolean }) {
      state.publishReady = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchWasteAdjustments.pending, state => {
        state.adjustmentsLoading = true
        state.error = null
      })
      .addCase(fetchWasteAdjustments.fulfilled, (state, action) => {
        state.adjustmentsLoading = false
        const data = action.payload?.data
        state.adjustments = Array.isArray(data) ? data : []
      })
      .addCase(fetchWasteAdjustments.rejected, (state, action) => {
        state.adjustmentsLoading = false
        state.error = action.error.message ?? 'Failed to fetch waste adjustments'
      })
      .addCase(saveWasteDayEntries.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(saveWasteDayEntries.fulfilled, (state, action) => {
        state.saveLoading = false
        if (!state.savedDays.includes(action.payload)) {
          state.savedDays.push(action.payload)
        }
        state.success = `Waste saved for ${action.payload}`
      })
      .addCase(saveWasteDayEntries.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to save waste entries'
      })
      .addCase(submitWasteEntries.pending, state => {
        state.submitLoading = true
        state.success = null
        state.error = null
      })
      .addCase(submitWasteEntries.fulfilled, state => {
        state.submitLoading = false
        state.isPublished = true
        state.success = 'Waste log published successfully'
      })
      .addCase(submitWasteEntries.rejected, (state, action) => {
        state.submitLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to publish waste log'
      })
      .addCase(fetchWasteLog.pending, state => {
        state.wasteLogLoading = true
      })
      .addCase(fetchWasteLog.fulfilled, (state, action) => {
        state.wasteLogLoading = false
        state.wasteLogDate = action.payload.date

        // Full list for the selected date (all active rows), including every append from Log Waste saves
        state.wasteLogEntries = Array.isArray(action.payload.data) ? action.payload.data : []
        if (state.wasteLogEntries.length > 0 && action.payload.date && !state.savedDays.includes(action.payload.date)) {
          state.savedDays.push(action.payload.date)
        }
      })
      .addCase(fetchWasteLog.rejected, state => {
        state.wasteLogLoading = false
        state.wasteLogEntries = []
        state.wasteLogDate = null
      })
      .addCase(fetchWasteLogForPostingPeriod.pending, state => {
        state.wasteLogPeriodLoading = true
      })
      .addCase(fetchWasteLogForPostingPeriod.fulfilled, (state, action) => {
        state.wasteLogPeriodLoading = false
        state.wasteLogPeriodStartDate = action.payload.startDate
        state.wasteLogPeriodEndDate = action.payload.endDate
        state.wasteLogPeriodEntries = Array.isArray(action.payload.data) ? action.payload.data : []
        for (const entry of state.wasteLogPeriodEntries) {
          const date = entry.transactionDate?.trim()
          if (date && !state.savedDays.includes(date)) {
            state.savedDays.push(date)
          }
        }
      })
      .addCase(fetchWasteLogForPostingPeriod.rejected, state => {
        state.wasteLogPeriodLoading = false
        state.wasteLogPeriodEntries = []
        state.wasteLogPeriodStartDate = null
        state.wasteLogPeriodEndDate = null
      })

      .addCase(fetchPrimaryStorageCodes.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object') {
          state.primaryStorageCodes = { ...state.primaryStorageCodes, ...action.payload }
        }
      })
      .addCase(saveWasteLog.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(saveWasteLog.fulfilled, (state, action) => {
        state.saveLoading = false
        if (action.payload?.date && !state.savedDays.includes(action.payload.date)) {
          state.savedDays.push(action.payload.date)
        }
        state.wasteLogDate = action.payload.date

        // Same full GET-derived list as fetchWasteLog (not POST-only); preserves all prior saves for the date
        state.wasteLogEntries = Array.isArray(action.payload.data) ? action.payload.data : []
        state.success = `Saved ${action.payload.savedCount} record${action.payload.savedCount !== 1 ? 's' : ''} for ${action.payload.date}`
      })
      .addCase(saveWasteLog.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to save waste log'
      })
      .addCase(updateWasteLog.pending, state => {
        state.saveLoading = true
        state.error = null
      })
      .addCase(updateWasteLog.fulfilled, (state, action) => {
        state.saveLoading = false
        state.success = `Waste log updated (${action.payload} record${action.payload !== 1 ? 's' : ''})`
      })
      .addCase(updateWasteLog.rejected, (state, action) => {
        state.saveLoading = false
        state.error = (action.payload as any)?.message ?? action.error.message ?? 'Failed to update waste log'
      })
      .addCase(fetchCrunchTimeLocations.pending, state => {
        state.locationsLoading = true
      })
      .addCase(fetchCrunchTimeLocations.fulfilled, (state, action) => {
        state.locationsLoading = false
        state.locations = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeLocations.rejected, state => {
        state.locationsLoading = false
      })
      .addCase(fetchCrunchTimeLocationByCode.pending, state => {
        state.locationDetailsLoading = true
      })
      .addCase(fetchCrunchTimeLocationByCode.fulfilled, (state, action) => {
        state.locationDetailsLoading = false
        state.locationDetails = action.payload
      })
      .addCase(fetchCrunchTimeLocationByCode.rejected, state => {
        state.locationDetailsLoading = false
      })
      .addCase(fetchCrunchTimeRecipes.pending, state => {
        state.recipesLoading = true
      })
      .addCase(fetchCrunchTimeRecipes.fulfilled, (state, action) => {
        state.recipesLoading = false
        state.recipes = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeRecipes.rejected, state => {
        state.recipesLoading = false
      })
      .addCase(fetchCrunchTimePackageTypes.pending, state => {
        state.packageTypesLoading = true
      })
      .addCase(fetchCrunchTimePackageTypes.fulfilled, (state, action) => {
        state.packageTypesLoading = false
        state.packageTypes = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimePackageTypes.rejected, state => {
        state.packageTypesLoading = false
      })
      .addCase(fetchCrunchTimeApplicationUsers.pending, state => {
        state.applicationUsersLoading = true
      })
      .addCase(fetchCrunchTimeApplicationUsers.fulfilled, (state, action) => {
        state.applicationUsersLoading = false
        state.applicationUsers = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeApplicationUsers.rejected, state => {
        state.applicationUsersLoading = false
      })
      .addCase(fetchCrunchTimeCompanyProducts.pending, state => {
        state.companyProductsLoading = true
      })
      .addCase(fetchCrunchTimeCompanyProducts.fulfilled, (state, action) => {
        state.companyProductsLoading = false
        state.companyProducts = Array.isArray(action.payload.data) ? action.payload.data : []
      })
      .addCase(fetchCrunchTimeCompanyProducts.rejected, state => {
        state.companyProductsLoading = false
      })
      .addCase(fetchWasteProducts.pending, state => {
        state.wasteProductsLoading = true
      })
      .addCase(fetchWasteProducts.fulfilled, (state, action) => {
        state.wasteProductsLoading = false
        state.wasteProducts = Array.isArray(action.payload.data) ? action.payload.data : []
        state.wasteProductsHydratedForConcept =
          String(action.meta.arg.concept ?? '')
            .trim()
            .toUpperCase() || 'AUWASTE'
      })
      .addCase(fetchWasteProducts.rejected, (state, action) => {
        state.wasteProductsLoading = false
        state.wasteProductsHydratedForConcept =
          String(action.meta.arg.concept ?? '')
            .trim()
            .toUpperCase() || 'AUWASTE'
      })
  }
})

export const { resetWasteLoggingState, setPublished, setPublishReady } = gygEonWasteLoggingSlice.actions
export default gygEonWasteLoggingSlice.reducer
